import React from 'react';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import '@/styles/insights.css';

/*
 * Small data visuals shared by the Overview and the Analytics tab. Hand-drawn
 * SVG/CSS on design tokens (no hex), so they follow dark, light and admin themes.
 * Rules every visual here follows:
 *   - values are written next to the mark; colour never carries meaning alone
 *   - each has a plain-language summary for screen readers
 *   - "no comparison" is shown as such, never as zero change
 */

export const SEV_ORDER = ['Critical', 'High', 'Medium', 'Low'];
export const sevVar = (s) => `var(--state-${String(s).toLowerCase()})`;

const fmtDay = (iso) => {
  const [, m, d] = String(iso).split('-');
  return `${d}/${m}`;
};

/** New items per day, stacked by severity (Critical at the base so it lines up across days). */
export function StackedDailyBars({ series, height = 132, label = 'New briefs per day' }) {
  const days = series || [];
  const max = Math.max(1, ...days.map((d) => d.total));
  const w = 100 / Math.max(days.length, 1);
  const total = days.reduce((a, d) => a + d.total, 0);
  const peak = days.reduce((a, d) => (d.total > a.total ? d : a), days[0] || { total: 0 });
  const sums = SEV_ORDER.map((s) => [s, days.reduce((a, d) => a + (d[s] || 0), 0)]);
  const summary = `${label}: ${total} briefs over ${days.length} days${peak.total ? `, busiest day ${peak.date} with ${peak.total}` : ''}. `
    + sums.map(([s, n]) => `${n} ${s}`).join(', ') + '.';

  return (
    <figure className="ins-stack">
      <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" role="img" aria-label={summary} style={{ height }}>
        {[0.25, 0.5, 0.75].map((g) => (
          <line key={g} x1="0" x2="100" y1={height * g} y2={height * g} className="ins-grid" vectorEffect="non-scaling-stroke" />
        ))}
        <line x1="0" x2="100" y1={height - 0.5} y2={height - 0.5} className="ins-axis" vectorEffect="non-scaling-stroke" />
        {days.map((d, i) => {
          let y = height;
          return (
            <g key={d.date}>
              <title>{`${d.date}: ${d.total} brief${d.total === 1 ? '' : 's'}${d.total ? ` (${SEV_ORDER.filter((s) => d[s]).map((s) => `${d[s]} ${s}`).join(', ')})` : ''}`}</title>
              <rect x={i * w} width={w} y="0" height={height} fill="transparent" />
              {SEV_ORDER.map((s) => {
                const h = d[s] ? Math.max(1.5, (d[s] / max) * (height - 3)) : 0;
                if (!h) return null;
                y -= h;
                return <rect key={s} x={i * w + w * 0.14} width={w * 0.72} y={y} height={h} fill={sevVar(s)} />;
              })}
            </g>
          );
        })}
      </svg>
      <div className="ins-axis-labels t-meta" aria-hidden="true">
        <span>{days[0] ? fmtDay(days[0].date) : ''}</span>
        <span>{days.length > 2 ? fmtDay(days[Math.floor(days.length / 2)].date) : ''}</span>
        <span>Today</span>
      </div>
      <figcaption className="ins-legend">
        {sums.map(([s, n]) => (
          <span key={s} className="ins-legend-item">
            <i style={{ background: sevVar(s) }} aria-hidden="true" />
            {s} <b>{n}</b>
          </span>
        ))}
        {peak.total > 0 && <span className="ins-legend-item ins-legend-peak">Peak {peak.total}/day</span>}
      </figcaption>
    </figure>
  );
}

/**
 * Ranked horizontal bars with the value written beside each. `emphasis` is the part of the bar
 * that deserves attention (for example the Critical/High share), drawn in the critical colour.
 */
export function RankedBars({ items, empty = 'Nothing to show for this period.', emphasisLabel }) {
  if (!items || !items.length) return <p className="ins-empty">{empty}</p>;
  const max = Math.max(1, ...items.map((i) => i.value));
  const hasEmphasis = Boolean(emphasisLabel) && items.some((i) => i.emphasis > 0);
  return (
    <>
      {hasEmphasis && (
        <p className="ins-ranked-key">
          <i className="is-em" aria-hidden="true" /> {emphasisLabel}
          <i aria-hidden="true" /> Other
        </p>
      )}
    <ol className="ins-ranked">
      {items.map((i) => (
        <li key={i.key || i.label}>
          <div className="ins-ranked-head">
            <span className="ins-ranked-label" title={i.label}>{i.label}</span>
            <span className="ins-ranked-val">{i.value}{i.suffix || ''}</span>
          </div>
          <div className="ins-track" role="presentation">
            <span className="ins-fill" style={{ width: `${Math.max(3, (i.value / max) * 100)}%`, background: i.color || 'var(--accent-from)' }}>
              {i.emphasis > 0 && <span className="ins-fill-em" style={{ width: `${Math.min(100, (i.emphasis / i.value) * 100)}%` }} />}
            </span>
          </div>
          {i.emphasis > 0 && emphasisLabel && <span className="sr-only">{i.emphasis} {emphasisLabel}</span>}
          {i.note && <div className="ins-ranked-note">{i.note}</div>}
        </li>
      ))}
    </ol>
    </>
  );
}

/** value of total as a labelled bar. */
export function ProgressBar({ value, total, tone = 'accent', label }) {
  const share = total ? Math.round((100 * value) / total) : 0;
  return (
    <div className="ins-progress" role="img" aria-label={`${label || 'Progress'}: ${value} of ${total} (${share}%)`}>
      <span style={{ width: `${share}%`, background: tone === 'accent' ? 'var(--accent-from)' : `var(--state-${tone})` }} />
    </div>
  );
}

/** One 100% bar split into labelled parts (severity mix, workflow stages). */
export function SplitBar({ parts, label }) {
  const total = parts.reduce((a, p) => a + p.value, 0);
  if (!total) return <p className="ins-empty">Nothing to show for this period.</p>;
  const summary = `${label}: ${parts.filter((p) => p.value).map((p) => `${p.value} ${p.label}`).join(', ')}.`;
  return (
    <div className="ins-split">
      <div className="ins-split-bar" role="img" aria-label={summary}>
        {parts.filter((p) => p.value).map((p) => (
          <span key={p.label} style={{ width: `${(100 * p.value) / total}%`, background: p.color }} title={`${p.label}: ${p.value}`} />
        ))}
      </div>
      <ul className="ins-split-legend">
        {parts.map((p) => (
          <li key={p.label}>
            <i style={{ background: p.color }} aria-hidden="true" />
            <span>{p.label}</span>
            <b>{p.value}</b>
            <span className="ins-split-pct">{total ? `${Math.round((100 * p.value) / total)}%` : ''}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Change against the previous period. `previous == null` means "cannot compare" (not enough
 * history), which is shown as such. For threat counts an increase is bad, hence `inverse`.
 */
export function Delta({ current, previous, inverse = true, suffix = 'vs previous' }) {
  if (previous == null) return <span className="ins-delta ins-delta--na">No comparison yet</span>;
  const diff = current - previous;
  if (diff === 0) return <span className="ins-delta"><Minus aria-hidden="true" /> No change {suffix}</span>;
  const worse = inverse ? diff > 0 : diff < 0;
  const Icon = diff > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`ins-delta ${worse ? 'ins-delta--bad' : 'ins-delta--good'}`}>
      <Icon aria-hidden="true" /> {diff > 0 ? '+' : '-'}{Math.abs(diff)} {suffix}
    </span>
  );
}
